import { Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from '@halo/web-lib';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AdminLayout } from './components/AdminLayout';
import { VersionBadge } from './components/VersionBadge';
import { DashboardPage } from './pages/DashboardPage';
import { StoreListView, StoreDetailView } from './pages/StoresPage';
import { UsersPage } from './pages/UsersPage';
import { UserDetailPage } from './pages/UserDetailPage';
import {
  IngestionStoreList,
  IngestionStoreConfigs,
  IngestionConfigDetail,
  IngestionRunDetail,
} from './pages/ingestion';
import { JobsPage } from './pages/JobsPage';
import { FailedJobsPage } from './pages/FailedJobsPage';
import { CircuitBreakersPage } from './pages/CircuitBreakersPage';
import { ServicesPage } from './pages/ServicesPage';
import { OperationsPage } from './pages/operations/OperationsPage';
import { ShopifyParityPage } from './pages/ShopifyParityPage';
import { ShopifyDiagnosticPage } from './pages/ShopifyDiagnosticPage';
import { ConnectionHealthPage } from './pages/ConnectionHealthPage';
import { AgencyClientsPage } from './pages/AgencyClientsPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { HaloMmmPage } from './pages/HaloMmmPage';
import { HaloV2Page } from './pages/halo-v2/HaloV2Page';
import { SlackPage } from './pages/SlackPage';
import { SchedulerPage } from './pages/SchedulerPage';
import { CloudRunJobsPage } from './pages/CloudRunJobsPage';
import { CloudTasksPage } from './pages/CloudTasksPage';
import { FeatureFlagsPage } from './pages/FeatureFlagsPage';
import { AlertsPage } from './pages/AlertsPage';
import { AnomaliesPage } from './pages/AnomaliesPage';
import { MonitoringPage } from './pages/MonitoringPage';
import { SlaPage } from './pages/SlaPage';
import { DataOpsPage } from './pages/data-ops/DataOpsPage';
import MaintenancePage from './pages/MaintenancePage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { TooltipProvider } from './components/ui/Tooltip';

function ErrorFallback({ resetError }: { readonly resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
          Something went wrong
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          An unexpected error occurred. Please try reloading the page.
        </p>
        <button
          type="button"
          onClick={resetError}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // RoleType.ADMIN = 0
  if (user?.role !== '0') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            This panel is restricted to admin users.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/*"
          element={
            <RequireAdmin>
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/agency" element={<AgencyClientsPage />} />
                  <Route path="/stores" element={<StoreListView />} />
                  <Route path="/stores/:id" element={<StoreDetailView />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/:id" element={<UserDetailPage />} />
                  <Route path="/api-keys" element={<ApiKeysPage />} />
                  <Route path="/ingestion" element={<IngestionStoreList />} />
                  <Route path="/ingestion/:storeId" element={<IngestionStoreConfigs />} />
                  <Route path="/ingestion/:storeId/:configId" element={<IngestionConfigDetail />} />
                  <Route
                    path="/ingestion/:storeId/:configId/runs/:runId"
                    element={<IngestionRunDetail />}
                  />
                  <Route path="/jobs" element={<JobsPage />} />
                  <Route path="/failed-jobs" element={<FailedJobsPage />} />
                  <Route path="/circuit-breakers" element={<CircuitBreakersPage />} />
                  <Route path="/operations" element={<OperationsPage />} />
                  <Route path="/shopify-parity" element={<ShopifyParityPage />} />
                  <Route path="/shopify-diagnostic" element={<ShopifyDiagnosticPage />} />
                  <Route path="/connection-health" element={<ConnectionHealthPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/halo-mmm" element={<HaloMmmPage />} />
                  <Route path="/halo-v2" element={<HaloV2Page />} />
                  <Route path="/slack" element={<SlackPage />} />
                  <Route path="/scheduler" element={<SchedulerPage />} />
                  <Route path="/cloud-run-jobs" element={<CloudRunJobsPage />} />
                  <Route path="/cloud-tasks" element={<CloudTasksPage />} />
                  <Route path="/sla" element={<SlaPage />} />
                  <Route path="/data-ops" element={<DataOpsPage />} />
                  <Route path="/feature-flags" element={<FeatureFlagsPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/anomalies" element={<AnomaliesPage />} />
                  <Route path="/monitoring" element={<MonitoringPage />} />
                  <Route path="/maintenance" element={<MaintenancePage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AdminLayout>
            </RequireAdmin>
          }
        />
      </Routes>
      <VersionBadge />
    </>
  );
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <TooltipProvider delayDuration={200}>
        <AppRoutes />
      </TooltipProvider>
    </Sentry.ErrorBoundary>
  );
}
